/*
 * slideshow-schedule.js
 *
 * Live scheduling for the slides in sections/slideshow.liquid.
 *
 * WHY THIS EXISTS
 * Liquid resolves the visible set once, server-side, at render time. Full-page /
 * edge cache then freezes that render, so a page cached before a schedule boundary
 * and served after it shows a stale slide set. Worse, the *decision of when to
 * flip* used to be a Liquid-computed epoch (`data-next-boundary`), and re-parsing a
 * bare wall-clock string with `| date: '%s'` did not reliably re-apply the shop's
 * UTC offset — so on a UTC+9 (JST) store like Rikumo the boundary handed to the
 * browser could be off by up to nine hours. The timer fired at the wrong moment, or
 * so far off it never armed, and slides flipped at the wrong time or not at all.
 *
 * FIX
 * The comparison is now entirely client-side and timezone-consistent:
 *   - Liquid emits the shop UTC offset (`data-store-offset`, e.g. "+09:00") and a
 *     JSON schedule for EVERY scheduled slide (including ones hidden right now), as
 *     offset-qualified ISO strings. `new Date("2026-05-01T09:30:00+09:00")` is the
 *     same absolute instant in every visitor's browser, whatever their local zone.
 *   - This script computes visibility and the true next boundary itself. On load it
 *     detects whether the rendered set is already stale (cache) and corrects it; it
 *     arms a SINGLE timer to the nearest future boundary and re-arms for the next one
 *     after each flip — never looping on the same boundary.
 *
 * HOW A FLIP IS APPLIED (no flicker, no layout shift)
 * At a boundary we re-fetch this section fresh (unique URL + cache: 'no-store' to
 * bypass edge/browser cache) and, only if the visible set actually changed, replace
 * the root in place. That re-runs <slideshow-component>'s constructor so slides /
 * dots / counter / sizing rebuild cleanly — no full reload, no scroll jump. A single
 * guarded page reload remains ONLY as a last resort if an aggressive CDN keeps
 * serving a stale render after retries.
 *
 * Purely additive. First paint and no-JS behavior are unchanged.
 */
(function () {
  'use strict';

  // The theme editor re-renders sections on its own; don't interfere there.
  if (window.Shopify && window.Shopify.designMode) return;

  var DEFAULT_POLL_SECONDS = 60;
  var MIN_POLL_SECONDS = 15;
  var MAX_TIMER_MS = 12 * 60 * 60 * 1000; // setTimeout can't be trusted for far-off boundaries
  var BOUNDARY_BUFFER_MS = 1500; // clear the server-clock edge before fetching
  var BOUNDARY_RETRIES = 3; // fetch retries before the reload fallback
  var RETRY_SPACING_MS = 3000;

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  // Parse "+09:00" / "-0530" → { ms, str }. `str` is normalized to colon form for
  // reuse in Date.parse; `ms` is the offset in milliseconds. Falls back to the
  // visitor's own zone if the attribute is missing/malformed (best effort — the
  // fixed-date windows already carry their offset inline and don't rely on this).
  function parseOffset(raw) {
    var m = /^([+-])(\d{2}):?(\d{2})$/.exec((raw || '').trim());
    if (!m) {
      var localMin = -new Date().getTimezoneOffset(); // minutes east of UTC
      var sign = localMin < 0 ? '-' : '+';
      var abs = Math.abs(localMin);
      return {
        ms: localMin * 60000,
        str: sign + pad2(Math.floor(abs / 60)) + ':' + pad2(abs % 60),
      };
    }
    var totalMin = (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * (m[1] === '-' ? -1 : 1);
    return { ms: totalMin * 60000, str: m[1] + m[2] + ':' + m[3] };
  }

  function bind(root) {
    if (!root || root.dataset.scheduleBound === 'true') return;
    root.dataset.scheduleBound = 'true';

    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    var pollSeconds = parseInt(root.getAttribute('data-poll-seconds'), 10);
    if (!pollSeconds || pollSeconds < MIN_POLL_SECONDS) pollSeconds = DEFAULT_POLL_SECONDS;

    // `current` always points at the live root; reassigned after each swap so the one
    // interval / timer keeps working against the latest element and its fresh schedule.
    var current = root;
    var inFlight = false;
    var boundaryTimer = null;

    function readOffset() {
      return parseOffset(current.getAttribute('data-store-offset'));
    }

    // The schedule JSON for all scheduled slides, read from the current root.
    function readSchedule() {
      var el = current.querySelector('.slideshow-schedule-data');
      if (!el) return [];
      try {
        var parsed = JSON.parse(el.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }

    // Store-local wall clock ("HH:MM") for `now`, using the shop offset.
    function storeHm(now, offset) {
      var shifted = new Date(now + offset.ms);
      return pad2(shifted.getUTCHours()) + ':' + pad2(shifted.getUTCMinutes());
    }

    // Absolute epoch (ms) of `hm` on the store-local day `dayOffset` days from now.
    function dailyEpoch(now, offset, dayOffset, hm) {
      var shifted = new Date(now + offset.ms);
      shifted.setUTCDate(shifted.getUTCDate() + dayOffset);
      var ymd =
        shifted.getUTCFullYear() +
        '-' +
        pad2(shifted.getUTCMonth() + 1) +
        '-' +
        pad2(shifted.getUTCDate());
      return Date.parse(ymd + 'T' + hm + ':00' + offset.str);
    }

    // Evaluate one slide. Mirrors the Liquid semantics exactly (minute granularity,
    // inclusive at both the start minute and the end minute). Returns whether it
    // should be visible now and the next epoch (ms) at which that answer changes.
    function evalSlide(s, now, offset) {
      var visible = true;
      var next = Infinity;
      var hasFixed = !!(s.start || s.end);

      if (hasFixed) {
        var nowMin = Math.floor(now / 60000) * 60000;
        var startMs = s.start ? Date.parse(s.start) : null;
        var endMs = s.end ? Date.parse(s.end) : null;
        var startOk = startMs == null || nowMin >= startMs;
        var endOk = endMs == null || nowMin <= endMs;
        visible = startOk && endOk;
        if (!startOk && startMs != null) {
          next = Math.min(next, startMs); // slide appears at its start minute
        } else if (visible && endMs != null) {
          next = Math.min(next, endMs + 60000); // slide hides the minute after its end
        }
        // Past the end with no recurrence: it never flips again → next stays Infinity.
      }

      if (s.dailyStart && s.dailyEnd) {
        var hm = storeHm(now, offset);
        var st = s.dailyStart;
        var et = s.dailyEnd;
        var dailyVisible = st <= et ? hm >= st && hm <= et : hm >= st || hm <= et;
        visible = hasFixed ? visible && dailyVisible : dailyVisible;

        // Nearest future flip across today/tomorrow (covers overnight windows too).
        for (var d = 0; d <= 1; d++) {
          var appear = dailyEpoch(now, offset, d, st);
          var hide = dailyEpoch(now, offset, d, et) + 60000;
          if (appear > now) next = Math.min(next, appear);
          if (hide > now) next = Math.min(next, hide);
        }
      }

      return { visible: visible, next: next };
    }

    // Which scheduled slide ids should be visible now, and the soonest boundary.
    function computeState() {
      var now = Date.now();
      var offset = readOffset();
      var schedule = readSchedule();
      var visibleNow = {};
      var next = Infinity;
      for (var i = 0; i < schedule.length; i++) {
        var r = evalSlide(schedule[i], now, offset);
        if (r.visible) visibleNow[schedule[i].id] = true;
        if (r.next < next) next = r.next;
      }
      return { schedule: schedule, visibleNow: visibleNow, next: next };
    }

    // Is the currently rendered set out of step with what should be visible now?
    // Compares each scheduled slide's desired visibility against what the server
    // actually rendered (its id is present in the visible signature). This is what
    // catches a cache-frozen page served across a boundary.
    function isStale(state) {
      var sig = current.getAttribute('data-visible-signature') || '';
      var rendered = {};
      var parts = sig ? sig.split(',') : [];
      for (var i = 0; i < parts.length; i++) rendered[parts[i]] = true;
      for (var j = 0; j < state.schedule.length; j++) {
        var id = state.schedule[j].id;
        if (!!state.visibleNow[id] !== !!rendered[id]) return true;
      }
      return false;
    }

    function fetchSection() {
      var url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_ts', String(Date.now())); // unique URL → bypass edge cache
      return fetch(url.toString(), {
        credentials: 'same-origin',
        cache: 'no-store', // bypass the browser HTTP cache
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then(function (res) {
        return res.ok ? res.text() : null;
      });
    }

    // Fetch a fresh render and swap in place. Resolves true only if the visible set
    // actually changed (swap applied), false otherwise. Leaves the slider untouched
    // when nothing changed, so autoplay / position are preserved.
    function tryRefresh() {
      if (!current.isConnected) return Promise.resolve(false);
      return fetchSection()
        .then(function (html) {
          if (!html || !current.isConnected) return false;

          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector(
            '[data-slideshow-schedule][data-section-id="' + sectionId + '"]'
          );
          if (!fresh) return false;

          var currentSig = current.getAttribute('data-visible-signature') || '';
          var freshSig = fresh.getAttribute('data-visible-signature') || '';
          if (freshSig === currentSig) return false; // set unchanged — leave slider alone

          fresh.dataset.scheduleBound = 'true';
          current.replaceWith(fresh); // upgrades the new <slideshow-component>
          current = fresh;

          if (typeof window.rikumoSlideshowVideoHydrate === 'function') {
            window.rikumoSlideshowVideoHydrate(current);
          }
          return true;
        })
        .catch(function () {
          return false;
        });
    }

    // Guarded single reload — never loops on the same boundary (sessionStorage guard,
    // keyed by the boundary epoch, survives the reload).
    function reloadOnce(boundaryKey) {
      var key = 'rikumoSlideshowReload:' + sectionId + ':' + boundaryKey;
      try {
        if (window.sessionStorage.getItem(key)) return;
        window.sessionStorage.setItem(key, '1');
      } catch (e) {
        /* private mode — skip the guard rather than risk a loop; just return */
        return;
      }
      window.location.reload();
    }

    // Boundary driver — fired at the exact scheduled moment (or on a stale-on-load
    // detection). Retries the fetch a few times, then falls back to one guarded
    // reload if the render is still stale (aggressive edge cache).
    function onBoundary(boundaryKey, attempt) {
      if (inFlight) return;
      inFlight = true;
      tryRefresh().then(function (changed) {
        inFlight = false;
        if (changed) {
          scheduleBoundary();
          return;
        }
        if (attempt < BOUNDARY_RETRIES) {
          window.setTimeout(function () {
            onBoundary(boundaryKey, attempt + 1);
          }, RETRY_SPACING_MS);
        } else {
          reloadOnce(boundaryKey);
        }
      });
    }

    // Low-frequency backstop for merchant edits and any drift the timer missed.
    function poll() {
      if (inFlight) return;
      inFlight = true;
      tryRefresh().then(function (changed) {
        inFlight = false;
        if (changed) scheduleBoundary();
      });
    }

    // Arm exactly one timer to the nearest future boundary. Re-armed after every flip
    // (for the next slide's boundary) and on focus — but only one timer is ever live.
    function scheduleBoundary() {
      if (boundaryTimer) {
        window.clearTimeout(boundaryTimer);
        boundaryTimer = null;
      }

      var state = computeState();

      // A page served stale from cache across a boundary: correct it right away, once.
      if (isStale(state)) {
        onBoundary('stale', 0);
        return;
      }

      if (!isFinite(state.next)) return; // no upcoming boundary (e.g. all past, or none)

      var delay = state.next - Date.now() + BOUNDARY_BUFFER_MS;
      var key = String(state.next);
      if (delay <= 0) {
        onBoundary(key, 0); // boundary already due — act now
        return;
      }
      if (delay > MAX_TIMER_MS) return; // too far out to arm; poll / focus re-arms as it nears
      boundaryTimer = window.setTimeout(function () {
        onBoundary(key, 0);
      }, delay);
    }

    scheduleBoundary();
    window.setInterval(poll, pollSeconds * 1000);

    // A backgrounded / inactive tab throttles timers; re-check the instant it's shown
    // again so a boundary crossed while hidden is applied immediately.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') scheduleBoundary();
    });
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) scheduleBoundary(); // restored from bfcache
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-slideshow-schedule]');
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
